import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { ImageUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Textarea } from '@/components/ui/input';
import { Field } from '@/components/triggers/Field';
import { api } from '@/lib/api';

export interface ProductDraft {
  name: string;
  brand: string;
  imageUrl: string;
  /** One label claim per line. */
  claimsText: string;
}

export const EMPTY_PRODUCT: ProductDraft = { name: '', brand: '', imageUrl: '', claimsText: '' };

export function claimsFrom(draft: ProductDraft): string[] {
  return draft.claimsText
    .split('\n')
    .map((c) => c.trim())
    .filter(Boolean);
}

export function SponsoredProductCard({
  enabled,
  onEnabledChange,
  draft,
  onChange,
}: {
  enabled: boolean;
  onEnabledChange: (on: boolean) => void;
  draft: ProductDraft;
  onChange: (next: ProductDraft) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadProductImage(file),
    onSuccess: (resp) => {
      onChange({ ...draft, imageUrl: resp.image_url });
      toast.success('Product image uploaded');
    },
    onError: (err) => toast.error('Upload failed', { description: String(err) }),
  });

  const missing: string[] = [];
  if (enabled) {
    if (!draft.name.trim()) missing.push('product name');
    if (!draft.imageUrl.trim()) missing.push('product image');
    if (claimsFrom(draft).length === 0) missing.push('at least one label claim');
  }

  return (
    <Card>
      <CardHeader>
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-primary"
          />
          <span className="space-y-1">
            <CardTitle>Feature a product (sponsored)</CardTitle>
            <CardDescription>
              The product stands beside the persona in every frame and is never handled. The script
              may only use its label claims, never personal results, and the persona says the
              approved lines word for word. The caption gets <code>#ad</code>. Use the custom script
              rules below for the story.
            </CardDescription>
          </span>
        </label>
      </CardHeader>
      {enabled && (
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Product name">
            <Input
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              placeholder="e.g. Inno Shred GLP-1"
            />
          </Field>
          <Field label="Brand">
            <Input
              value={draft.brand}
              onChange={(e) => onChange({ ...draft, brand: e.target.value })}
              placeholder="e.g. Inno Supps"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Product image (packshot)">
              <div className="flex items-center gap-3">
                {draft.imageUrl ? (
                  <img
                    src={draft.imageUrl}
                    alt="Product"
                    className="h-20 w-20 shrink-0 rounded-md border border-border bg-muted/30 object-contain"
                  />
                ) : (
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                    No image
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) upload.mutate(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={upload.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    <ImageUp className="h-3.5 w-3.5" />
                    {upload.isPending ? 'Uploading…' : 'Upload image'}
                  </Button>
                  <Input
                    value={draft.imageUrl}
                    onChange={(e) => onChange({ ...draft, imageUrl: e.target.value })}
                    placeholder="…or paste an image URL"
                  />
                </div>
              </div>
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Label claims (one per line)">
              <Textarea
                rows={3}
                value={draft.claimsText}
                onChange={(e) => onChange({ ...draft, claimsText: e.target.value })}
                placeholder={'Appetite support\nMetabolic wellness\nEnergy & focus'}
              />
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">
              Exactly as printed on the label. These are the only claims the script may make.
            </p>
          </div>
          {missing.length > 0 && (
            <p className="text-xs text-amber-700 md:col-span-2 dark:text-amber-300">
              Still needed: {missing.join(', ')}.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
